<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="$emit('close')"></div>
    
    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 transform transition-all">
        <!-- Close button -->
        <button 
          @click="$emit('close')"
          class="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <!-- Header -->
        <div class="text-center mb-6">
          <div class="mx-auto w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900">Contact Us</h3>
          <p class="text-sm text-gray-500 mt-1">We'd love to hear from you!</p>
        </div>
        
        <!-- Success Message -->
        <div v-if="success" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div class="flex items-center">
            <svg class="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <p class="text-sm font-medium text-green-800">Message sent successfully! We'll get back to you soon.</p>
          </div>
        </div>
        
        <!-- Error Message -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p class="text-sm text-red-800">{{ error }}</p>
        </div>
        
        <!-- Form -->
        <form v-if="!success" @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label for="contact-name" class="block text-sm font-medium text-gray-700 mb-1">
              Name <span class="text-red-500">*</span>
            </label>
            <input
              id="contact-name"
              v-model="form.name"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              placeholder="Your name"
            />
          </div>
          
          <div>
            <label for="contact-email" class="block text-sm font-medium text-gray-700 mb-1">
              Email <span class="text-red-500">*</span>
            </label>
            <input
              id="contact-email"
              v-model="form.email"
              type="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              placeholder="you@example.com"
            />
          </div>
          
          <div>
            <label for="contact-phone" class="block text-sm font-medium text-gray-700 mb-1">
              Phone <span class="text-gray-400 text-xs">(optional)</span>
            </label>
            <input
              id="contact-phone"
              v-model="form.phone"
              type="tel"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              placeholder="+1 (555) 123-4567"
            />
          </div>
          
          <div>
            <label for="contact-message" class="block text-sm font-medium text-gray-700 mb-1">
              Message <span class="text-red-500">*</span>
            </label>
            <textarea
              id="contact-message"
              v-model="form.message"
              required
              rows="4"
              maxlength="500"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
              placeholder="How can we help you?"
            ></textarea>
            <p class="text-xs text-gray-400 mt-1 text-right">{{ form.message.length }}/500</p>
          </div>
          
          <!-- Captcha Section -->
          <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Security Check <span class="text-red-500">*</span>
            </label>
            
            <div class="flex items-center space-x-3">
              <!-- Captcha Display -->
              <div class="flex-shrink-0 bg-white border border-gray-300 rounded-lg overflow-hidden">
                <div v-if="captchaLoading" class="w-[150px] h-[50px] flex items-center justify-center text-gray-400 text-sm">
                  Loading...
                </div>
                <div v-else-if="captcha.svg" v-html="captcha.svg" class="captcha-image"></div>
              </div>
              
              <!-- Refresh Button -->
              <button 
                type="button" 
                @click="refreshCaptcha"
                class="p-2 text-gray-500 hover:text-purple-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh captcha"
                :disabled="captchaLoading"
              >
                <svg class="w-5 h-5" :class="{ 'animate-spin': captchaLoading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            
            <input
              id="captcha-answer"
              v-model="form.captchaAnswer"
              type="text"
              required
              class="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              placeholder="Enter the characters above"
              autocomplete="off"
            />
            <p class="text-xs text-gray-400 mt-1">Type the characters shown in the image (case-insensitive)</p>
          </div>
          
          <button
            type="submit"
            :disabled="loading || !isFormValid || captchaLoading"
            class="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </span>
            <span v-else>Send Message</span>
          </button>

          <!-- Honeypot: hidden from real users, bots that autofill get caught -->
          <div class="honeypot-field" aria-hidden="true">
            <label for="contact-company">Company</label>
            <input
              id="contact-company"
              v-model="form.company"
              type="text"
              name="company"
              tabindex="-1"
              autocomplete="off"
            />
          </div>
        </form>
        
        <!-- Close button after success -->
        <button
          v-if="success"
          @click="$emit('close')"
          class="w-full py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from '@/utils/axios'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])

const form = ref({
  name: '',
  email: '',
  phone: '',
  message: '',
  captchaAnswer: '',
  company: '' // honeypot - must stay empty
})

const captcha = ref({
  svg: '',
  id: '',
  token: ''
})

const loading = ref(false)
const captchaLoading = ref(false)
const error = ref(null)
const success = ref(false)

const isFormValid = computed(() => {
  return form.value.name.trim() && 
         form.value.email.trim() && 
         form.value.message.trim() &&
         form.value.message.length <= 500 &&
         form.value.captchaAnswer.trim() &&
         captcha.value.id
})

// Fetch captcha from server
const fetchCaptcha = async () => {
  captchaLoading.value = true
  try {
    const response = await axios.get('/qrcloneapi/api/contact/captcha')
    captcha.value = {
      svg: response.data.svg,
      id: response.data.captchaId,
      token: response.data.captchaToken
    }
  } catch (err) {
    console.error('Failed to load captcha:', err)
    error.value = 'Failed to load security check. Please try again.'
  } finally {
    captchaLoading.value = false
  }
}

const refreshCaptcha = () => {
  form.value.captchaAnswer = ''
  error.value = null
  fetchCaptcha()
}

// Reset form when modal opens
watch(() => props.show, (newVal) => {
  if (newVal) {
    form.value = {
      name: '',
      email: '',
      phone: '',
      message: '',
      captchaAnswer: '',
      company: ''
    }
    error.value = null
    success.value = false
    fetchCaptcha()
  }
})

const handleSubmit = async () => {
  if (!isFormValid.value) return
  
  loading.value = true
  error.value = null
  
  try {
    await axios.post('/qrcloneapi/api/contact', {
      name: form.value.name.trim(),
      email: form.value.email.trim(),
      phone: form.value.phone.trim() || null,
      message: form.value.message.trim(),
      captchaId: captcha.value.id,
      captchaAnswer: form.value.captchaAnswer.trim(),
      captchaToken: captcha.value.token,
      company: form.value.company
    })
    
    success.value = true
  } catch (err) {
    const errorMessage = err.response?.data?.error || 'Failed to send message. Please try again.'
    error.value = errorMessage
    
    // If captcha error, refresh captcha
    if (errorMessage.toLowerCase().includes('captcha')) {
      refreshCaptcha()
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
/* Off-screen rather than display:none / type=hidden - some bots skip those */
.honeypot-field {
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.captcha-image :deep(svg) {
  display: block;
  max-width: 100%;
  height: auto;
}
</style>
